import { getOptionValue } from 'services/helpers';
import {
  checkDeleted,
  checkListDeleted,
  displayNameDescriptor,
} from 'pages/AutomationEngine/helpers';
import { AUTOMATION_STEPS } from 'pages/AutomationEngine/steps';
import type {
  GoalFieldUpdatedData,
  TriggerFieldUpdatedData,
  TriggerFieldUpdatedFromApi,
} from './types';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import {
  getValueForApi,
  getValueForFlow,
  getMultiValueForApi,
  getMultiValueForFlow,
} from './helpers';
import { FIELD_UPDATED_MODES } from './contants';

const triggerForApi = (orgDetails: TriggerFieldUpdatedData) => {
  const { field, fireOnCreate, fromMatchMode, toMatchMode } = orgDetails;
  let { fromValue, toValue } = orgDetails;

  switch (fromMatchMode) {
    case FIELD_UPDATED_MODES.SPECIFIC_VALUE:
      fromValue = getValueForApi(field?.fieldData, fromValue);
      break;
    case FIELD_UPDATED_MODES.DATE_BETWEEN:
      break;
    case FIELD_UPDATED_MODES.CONTAINS:
    case FIELD_UPDATED_MODES.DOES_NOT_CONTAIN:
      fromValue = getMultiValueForApi(field?.fieldData, fromValue);
      break;
    default:
      fromValue = null;
  }

  switch (toMatchMode) {
    case FIELD_UPDATED_MODES.SPECIFIC_VALUE:
      toValue = getValueForApi(field?.fieldData, toValue);
      break;
    case FIELD_UPDATED_MODES.DATE_BETWEEN:
      break;
    case FIELD_UPDATED_MODES.CONTAINS:
    case FIELD_UPDATED_MODES.DOES_NOT_CONTAIN:
    case FIELD_UPDATED_MODES.VALUES_ADDED:
    case FIELD_UPDATED_MODES.VALUES_REMOVED:
      toValue = getMultiValueForApi(field?.fieldData, toValue);
      break;
    default:
      toValue = null;
  }

  return {
    fieldId: field ? getOptionValue(field) : null,
    fireOnCreate,
    fromMatchMode,
    toMatchMode,
    fromValue,
    toValue,
  };
};

const triggerForFlow = (
  trigger: TriggerFieldUpdatedFromApi,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const { field: fieldObj, fireOnCreate, fromMatchMode, toMatchMode } = trigger;

  let { fromValue, toValue } = trigger;

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    fieldObj,
    displayNameDescriptor
  );

  const field = fieldObj
    ? {
        value: fieldObj.id,
        label: fieldObj.displayName,
        fieldData: fieldObj,
        deleted: fieldObj.deleted,
      }
    : null;

  switch (fromMatchMode) {
    case FIELD_UPDATED_MODES.SPECIFIC_VALUE: {
      fromValue = getValueForFlow(fieldObj, fromValue);
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        fromValue,
        ({ label }: { label: string }) => label
      );
      break;
    }
    case FIELD_UPDATED_MODES.CONTAINS:
    case FIELD_UPDATED_MODES.DOES_NOT_CONTAIN: {
      fromValue = getMultiValueForFlow(fieldObj, fromValue);
      checkListDeleted(
        messageDictionary,
        addErrorMessage,
        fromValue,
        ({ label }: { label: string }) => label
      );
      break;
    }
    default:
      break;
  }

  switch (toMatchMode) {
    case FIELD_UPDATED_MODES.SPECIFIC_VALUE: {
      toValue = getValueForFlow(fieldObj, toValue);
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        toValue,
        ({ label }: { label: string }) => label
      );
      break;
    }
    case FIELD_UPDATED_MODES.CONTAINS:
    case FIELD_UPDATED_MODES.DOES_NOT_CONTAIN:
    case FIELD_UPDATED_MODES.VALUES_ADDED:
    case FIELD_UPDATED_MODES.VALUES_REMOVED: {
      toValue = getMultiValueForFlow(fieldObj, toValue);
      checkListDeleted(
        messageDictionary,
        addErrorMessage,
        toValue,
        ({ label }: { label: string }) => label
      );
      break;
    }
    default:
      break;
  }

  return {
    field,
    fireOnCreate,
    fromMatchMode,
    toMatchMode,
    fromValue,
    toValue,
    type: AUTOMATION_STEPS.field_updated.type,
  };
};

const forApi = (orgDetails: GoalFieldUpdatedData) => {
  const {
    delayAmount = 0,
    waitType,
    delayType,
    specificDatetime,
    valueOrigin,
    variable,
    ...trigger
  } = orgDetails;

  return {
    stepGoal: {
      waitType,
      delayType,
      delayAmount,
      specificDatetime,
      valueOrigin,
      variable,
      triggers: [
        {
          type: AUTOMATION_STEPS.field_updated.type,
          trigger_field_updated: triggerForApi(trigger),
        },
      ],
    },
  };
};

const forFlow = (
  firstTrigger: { triggerFieldUpdated: TriggerFieldUpdatedFromApi },
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  return {
    ...triggerForFlow(
      firstTrigger.triggerFieldUpdated,
      addErrorMessage,
      messageDictionary
    ),
    type: AUTOMATION_STEPS.field_updated_goal.type,
  };
};

export const fieldUpdatedGoalDTO = {
  forApi,
  triggerForApi,
  forFlow,
  triggerForFlow,
};
